<template>
  <div>
    <headline-container headline="Create Person" :breadcrumbs="breadcrumbs"></headline-container>

    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <h3>
            Details
          </h3>

          <p>
            Enter the details of the person, including the name, email and basic contact info.
          </p>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit">
            <div class="panel panel-default">
              <div class="panel-body form-horizontal">
                <text-form-group
                    v-model="person.name"
                    name="name"
                    label="Name"
                    help-text="The name of the person."
                    :error="getError('name')"
                ></text-form-group>

                <text-form-group
                    v-model="person.title"
                    name="title"
                    label="Title"
                    help-text="The title of the person."
                    :error="getError('title')"
                ></text-form-group>

                <email-form-group
                    v-model="person.email"
                    name="email"
                    label="Email"
                    help-text="The person's email."
                    :error="getError('email')"
                ></email-form-group>

                <form-group
                    name="alternate_emails"
                    label="Alternate Emails"
                    help-text="Any alternate email addresses that the person uses."
                    :error="getError('alternate_emails')"
                >
                  <vue-tags-input
                      v-model="alternateEmails"
                      placeholder="Add email"
                      :tags="person.alternate_emails"
                      @tags-changed="$emit('input', person.alternate_emails = $event)"
                  ></vue-tags-input>
                </form-group>

                <text-form-group
                    v-model="person.display_title"
                    name="display_title"
                    label="Display Title"
                    help-text="The display title of the person."
                    :error="getError('display_title')"
                ></text-form-group>

                <text-form-group
                    v-model="person.cell_phone"
                    name="cell_phone"
                    label="Cell Phone"
                    help-text="The person's cell phone number."
                    :error="getError('cell_phone')"
                ></text-form-group>

                <text-form-group
                    v-model="person.work_phone"
                    name="work_phone"
                    label="Work Phone"
                    help-text="The person's work phone number."
                    :error="getError('work_phone')"
                ></text-form-group>

                <radio-form-group
                    v-model="person.has_trigger_mails"
                    name="has_trigger_mails"
                    label="Has Trigger Mails"
                    help-text="A flag indicating if trigger mails are enabled."
                    :error="getError('has_trigger_mails')"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                ></radio-form-group>

                <tags-form-group
                    v-model="tags"
                    name="tags"
                    label="Tags"
                    help-text="Any number of tags that describe the company."
                    :error="getError('tags')"
                ></tags-form-group>

                <GalleryFormGroup
                    v-model="person.image_id"
                    name="image_id"
                    label="Image"
                    help-text="The image of the person."
                    :error="(errors.image_id || [])[0]"
                    :nullable="true"
                />

                <typeahead-form-group
                    v-model="person.company_id"
                    name="company_id"
                    label="Company"
                    help-text="The company that the user is part of (optional)."
                    :error="getError('company_id')"
                    endpoint="/api/v2/companies"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="row => row.attributes.name"
                ></typeahead-form-group>

                <table-form-group
                    v-model="selectedPortals"
                    name="portals"
                    label="Portals"
                    help-text="The portals that the person is relevant for."
                    :error="getError('portals')"
                    :options="portalOptions"
                    option-label="Portal"
                ></table-form-group>
              </div>
            </div>

            <address-panel
                v-model="person.address"
                :toggleable="true"
                :countries="countries"
                :errors="errors"
            ></address-panel>

            <hr class="panel-separator">

            <button type="submit" class="btn btn-black" style="margin-bottom: 22px;">
              <i class="fa fa-save"></i> Save
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import AddressPanel from '@/components/AddressPanel'
import Errors from '@/mixins/Errors'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import FormGroup from '@/vendor/forms/FormGroups/Horizontal/FormGroup'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TableFormGroup from '@/components/FormGroups/Horizontal/Table'
import TagsFormGroup from '@/components/FormGroups/Horizontal/Tags'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Horizontal/Typeahead'
import VueTagsInput from '@johmun/vue-tags-input'
import GalleryFormGroup from "../../../components/FormGroups/Horizontal/Gallery"

export default {
  components: {
    GalleryFormGroup,
    AddressPanel,
    EmailFormGroup,
    FormGroup,
    HeadlineContainer,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    TableFormGroup,
    TagsFormGroup,
    TextFormGroup,
    TypeaheadFormGroup,
    VueTagsInput
  },

  mixins: [Errors, Load],

  data() {
    return {
      person: {
        address: {
          country: 'DK'
        },

        alternate_emails: [],

        has_trigger_mails: true,
      },
      tags: [],
      portals: [],
      selectedPortals: [],
      shared: store,
      alternateEmails: ''
    }
  },

  mounted() {
    this.load({
      portals: '/api/v2/portals'
    })
  },

  watch: {
    'person.company_id'(value) {
      if (value) {
        http.get('/api/v2/companies/' + value).then(response => {
          this.person.address = response.data.data.attributes.address
        })
      }
    }
  },

  computed: {
    breadcrumbs() {
      return [
        {url: '/people', text: 'People'},
        {text: 'Create'}
      ]
    },

    portalOptions() {
      return this.portals.data.map(portal => {
        return {
          value: portal.id,
          label: portal.attributes.name
        }
      })
    },

    requestData() {
      return {
        ...this.person,
        tags: this.tags.map(tag => tag.text),
        alternate_emails: this.person.alternate_emails.map(email => email.text),
        portals: this.selectedPortals.map(portal => {
          return {id: portal}
        })
      }
    }
  },

  methods: {
    submit() {
      http.post('/api/v1/people', this.requestData)
          .then(() => {
            notify.success('The person was saved')

            this.$router.push('/people')
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
